/**
 * Iconografía Material Symbols, en SVG en línea.
 *
 * El diseño original los carga como fuente desde Google Fonts. Aquí no, por
 * dos razones que apuntan en la misma dirección:
 *
 *   · El servidor de la caseta opera sin internet. Una fuente de iconos que no
 *     carga deja la interfaz llena de cuadritos, o peor, con el nombre del
 *     icono en texto —"account_circle"— porque así funcionan las ligaduras.
 *   · La fuente variable completa pesa ~3.4 MB para las quince figuras que
 *     realmente se usan.
 *
 * Los trazos son los de Material, así que el resultado se ve igual que el
 * diseño. Agregar uno nuevo es pegar su `path` aquí.
 */

type Props = {
  /** Tamaño en px. 20 para texto, 24 por omisión, 32+ para destacar. */
  tam?: number;
  className?: string;
};

function Svg({ tam = 24, className = '', d }: Props & { d: string }) {
  return (
    <svg
      width={tam}
      height={tam}
      viewBox="0 0 24 24"
      fill="currentColor"
      // Decorativos: el significado siempre está en el texto que acompaña, o
      // en un aria-label del botón que los contiene.
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d={d} />
    </svg>
  );
}

export const Iconos = {
  panel: (p: Props) => (
    <Svg {...p} d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
  ),

  personas: (p: Props) => (
    <Svg
      {...p}
      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
    />
  ),

  aparatos: (p: Props) => (
    <Svg
      {...p}
      d="M20.2 5.9l.8-.8C19.6 3.7 17.8 3 16 3s-3.6.7-5 2.1l.8.8C13 4.8 14.5 4.2 16 4.2s3 .6 4.2 1.7zm-.9.8c-.9-.9-2.1-1.4-3.3-1.4s-2.4.5-3.3 1.4l.8.8c.7-.7 1.6-1 2.5-1 .9 0 1.8.3 2.5 1l.8-.8zM19 13h-2V9h-2v4H5c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2zM8 18H6v-2h2v2zm3.5 0h-2v-2h2v2zm3.5 0h-2v-2h2v2z"
    />
  ),

  etiqueta: (p: Props) => (
    <Svg
      {...p}
      d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z"
    />
  ),

  unidades: (p: Props) => (
    <Svg
      {...p}
      d="M17 11V3H7v4H3v14h8v-4h2v4h8V11h-4zM7 19H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm4 4H9v-2h2v2zm0-4H9V9h2v2zm0-4H9V5h2v2zm4 8h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm4 12h-2v-2h2v2zm0-4h-2v-2h2v2z"
    />
  ),

  vehiculo: (p: Props) => (
    <Svg
      {...p}
      d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"
    />
  ),

  credencial: (p: Props) => (
    <Svg
      {...p}
      d="M20 7h-5V4c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2v3H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm-9-3h2v5h-2V4zm3 13H7v-1.5c0-1 2-1.5 3.5-1.5s3.5.5 3.5 1.5V17zm-3.5-4c-.83 0-1.5-.67-1.5-1.5S9.67 10 10.5 10s1.5.67 1.5 1.5S11.33 13 10.5 13zM19 17h-3v-2h3v2zm0-4h-3v-2h3v2z"
    />
  ),

  salir: (p: Props) => (
    <Svg
      {...p}
      d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"
    />
  ),

  entrar: (p: Props) => (
    <Svg
      {...p}
      d="M11 7L9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v14z"
    />
  ),

  menu: (p: Props) => <Svg {...p} d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />,

  avisos: (p: Props) => (
    <Svg
      {...p}
      d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
    />
  ),

  cuenta: (p: Props) => (
    <Svg
      {...p}
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
    />
  ),

  buscar: (p: Props) => (
    <Svg
      {...p}
      d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
    />
  ),

  agregar: (p: Props) => <Svg {...p} d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />,

  correcto: (p: Props) => (
    <Svg
      {...p}
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
    />
  ),

  alerta: (p: Props) => (
    <Svg
      {...p}
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
    />
  ),

  copiar: (p: Props) => (
    <Svg
      {...p}
      d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
    />
  ),

  anterior: (p: Props) => <Svg {...p} d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />,
  siguiente: (p: Props) => <Svg {...p} d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />,

  telefono: (p: Props) => (
    <Svg
      {...p}
      d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
    />
  ),

  cerrar: (p: Props) => (
    <Svg
      {...p}
      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
    />
  ),
} as const;

export type NombreIcono = keyof typeof Iconos;
